export type ChartCountry = "ALL" | "AT" | "BE" | "CH" | "DE" | "FR" | "HU" | "IT" | "NL";

export type DiveDeepChartMetric = "delay" | "cancellation";

export const EXCLUDED_DATES = new Set<string>();

export const CHART_COUNTRIES: ChartCountry[] = ["ALL", "AT", "BE", "CH", "DE", "FR", "HU", "IT", "NL"];

export const COUNTRY_FLAGS: Record<Exclude<ChartCountry, "ALL">, string> = {
  AT: "🇦🇹",
  BE: "🇧🇪",
  CH: "🇨🇭",
  DE: "🇩🇪",
  FR: "🇫🇷",
  HU: "🇭🇺",
  IT: "🇮🇹",
  NL: "🇳🇱",
};

export const COUNTRY_NAME_KEYS: Record<Exclude<ChartCountry, "ALL">, string> = {
  AT: "austria",
  BE: "belgium",
  CH: "switzerland",
  DE: "germany",
  FR: "france",
  HU: "hungary",
  IT: "italy",
  NL: "netherlands",
};

export type SummaryRow = {
  stopcountry: string;
  date: string;
  avg_delay_s: number;
  cancel_rate: number;
  max_temperature_c: number;
};

export function parseHeatwaveSummaryCsv(text: string): SummaryRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^\uFEFF/, ""));
  return lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] ?? "";
    });
    return {
      stopcountry: row.stopcountry,
      date: row.date,
      avg_delay_s: parseFloat(row.avg_delay_s),
      cancel_rate: parseFloat(row.cancel_rate),
      max_temperature_c: parseFloat(row.max_temperature_c),
    };
  });
}

export function secondsToMinutes(seconds: number): number {
  return seconds / 60;
}

export function roundUpTo5(value: number): number {
  return Math.ceil(value / 5) * 5;
}

export function roundDownTo5(value: number): number {
  return Math.floor(value / 5) * 5;
}

export type SensitivitySummaryRow = {
  stopcountry: string;
  delay_below_30_s: number;
  delay_above_30_s: number;
  delay_multiplier: number;
  extra_delay_per_average_hot_day: number;
  avg_trips_per_day: number;
  avg_total_records: number;
  cancel_below_30_pct: number | null;
  cancel_above_30_pct: number | null;
  cancel_multiplier: number | null;
  extra_cancelled_stopovers: number | null;
};

function parseOptionalFloat(raw: string | undefined): number | null {
  if (raw == null || raw === "") return null;
  const value = parseFloat(raw);
  return isNaN(value) ? null : value;
}

export function parseHeatwaveSensitivityCsv(text: string): SensitivitySummaryRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^\uFEFF/, ""));
  return lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] ?? "";
    });

    return {
      stopcountry: row.stopcountry,
      delay_below_30_s: parseFloat(row.delay_below_30_s),
      delay_above_30_s: parseFloat(row.delay_above_30_s),
      delay_multiplier: parseFloat(row.delay_multiplier),
      extra_delay_per_average_hot_day: parseFloat(row.extra_delay_per_average_hot_day),
      avg_trips_per_day: parseFloat(row.avg_trips_per_day),
      avg_total_records: parseFloat(row.avg_total_records),
      cancel_below_30_pct: parseOptionalFloat(row.cancel_below_30_pct),
      cancel_above_30_pct: parseOptionalFloat(row.cancel_above_30_pct),
      cancel_multiplier: parseOptionalFloat(row.cancel_multiplier),
      extra_cancelled_stopovers: parseOptionalFloat(row.extra_cancelled_stopovers),
    };
  });
}

export function multiplierToIncreasePct(multiplier: number): number {
  return Math.round((multiplier - 1) * 100);
}

export function formatMultiplierChangePct(multiplier: number): string {
  const pct = multiplierToIncreasePct(multiplier);
  if (pct === 0) {
    return "0%";
  }
  return pct > 0 ? `+${pct}%` : `${pct}%`;
}

export type HeatwaveStationMapRow = {
  uic: string;
  name: string;
  latitude: number;
  longitude: number;
  avgDelayBelow30S: number;
  avgDelayAbove30S: number;
  delayMultiplier: number;
  nBelow: number;
  nAbove: number;
};

const MIN_STATION_SAMPLE_SIZE = 2000;
const MIN_DELAY_CHANGE_PCT = -100;
const MAX_DELAY_CHANGE_PCT = 1000;
const MIN_DELAY_MULTIPLIER = 1 + MIN_DELAY_CHANGE_PCT / 100;
const MAX_DELAY_MULTIPLIER = 1 + MAX_DELAY_CHANGE_PCT / 100;

export { MIN_STATION_SAMPLE_SIZE };

export function parseHeatwaveStationMapCsv(text: string): HeatwaveStationMapRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^\uFEFF/, ""));
  return lines.slice(1).flatMap((line) => {
    const values = line.split(",").map((v) => v.trim());
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] ?? "";
    });

    const latitude = parseFloat(row.latitude);
    const longitude = parseFloat(row.longitude);
    const delayMultiplier = parseFloat(row.delay_multiplier);
    const nBelow = parseInt(row.n_below, 10) || 0;
    const nAbove = parseInt(row.n_above, 10) || 0;
    if (
      isNaN(latitude) ||
      isNaN(longitude) ||
      isNaN(delayMultiplier) ||
      nBelow + nAbove < MIN_STATION_SAMPLE_SIZE ||
      delayMultiplier < MIN_DELAY_MULTIPLIER ||
      delayMultiplier > MAX_DELAY_MULTIPLIER
    ) {
      return [];
    }

    return [
      {
        uic: row.uic,
        name: row.name,
        latitude,
        longitude,
        avgDelayBelow30S: parseFloat(row.avg_delay_below_30_s),
        avgDelayAbove30S: parseFloat(row.avg_delay_above_30_s),
        delayMultiplier,
        nBelow,
        nAbove,
      },
    ];
  });
}

const UIC_COUNTRY_KEYS: Record<string, string> = {
  "80": "germany",
  "81": "austria",
  "83": "italy",
  "84": "netherlands",
  "85": "switzerland",
  "87": "france",
  "88": "belgium",
  "55": "hungary",
};

export function countryNameFromUic(
  uic: string,
  t: (key: string) => string
): string {
  const countryKey = UIC_COUNTRY_KEYS[uic.slice(0, 2)];
  return countryKey
    ? t(`heatwave.countryNames.${countryKey}`)
    : t("heatwave.countryNames.unknown");
}
